import { produce } from 'immer'
import { create } from 'zustand'

import type { CatalogQueryPanel } from 'features/catalog/types'
import { generateId } from 'features/charts-v2/store'
import type { Condition } from 'features/charts-v2/types/yogiql'
import {
  defaultFeedbackQuery,
  type SearchFields,
} from 'features/feedback/feedback-query'

// ---------------------------------------------------------------------------
// Under `enhanced_dialect`, the catalog page's filter state: one YogiQL `query`
// in place of `useCatalogFiltersStore`'s single v2 panel. Always exactly one
// panel, as there. Mirrors keywords' `useKeywordsQueryStore`.
// ---------------------------------------------------------------------------

export type CatalogQuerySnapshot = {
  panels: CatalogQueryPanel[]
  activePanelId: string | null
  /** Eye-toggled-off conditions per panel, by the v2 field they read as.
   *  UI-only, never saved, as `useCatalogFiltersStore.ghostFilters` is. */
  ghosts: Record<string, Record<string, Condition[]>>
  /** The search cards the sidebar last set, so an empty card (no condition
   *  yet) stays on screen while it still agrees with the query. UI-only. */
  shownSearch: Record<string, SearchFields>
}

export type CatalogQueryState = CatalogQuerySnapshot & {
  hydrate: (state: {
    panels: CatalogQueryPanel[]
    activePanelId: string | null
  }) => void
  setFilters: (panelId: string, filters: Condition[]) => void
  setGhosts: (panelId: string, ghosts: Record<string, Condition[]>) => void
  setShownSearch: (panelId: string, search: SearchFields) => void
  /** Wholesale replace — used only by undo/redo. */
  restore: (state: CatalogQuerySnapshot) => void
  /** A fresh panel: the default date window and nothing else. */
  resetCatalogQuery: () => void
}

const freshState = (): CatalogQuerySnapshot => {
  const id = generateId()
  return {
    panels: [{ id, query: defaultFeedbackQuery() }],
    activePanelId: id,
    ghosts: {},
    shownSearch: {},
  }
}

const dropStale = <T>(
  byPanel: Record<string, T>,
  panels: CatalogQueryPanel[],
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(byPanel).filter(([id]) => panels.some((p) => p.id === id)),
  )

export const useCatalogQueryStore = create<CatalogQueryState>()((set, get) => ({
  ...freshState(),

  hydrate: ({ panels, activePanelId }) => {
    // A hash pop restores the same panel id, so its ghosts survive.
    set({
      panels,
      activePanelId,
      ghosts: dropStale(get().ghosts, panels),
      shownSearch: dropStale(get().shownSearch, panels),
    })
  },

  setFilters: (panelId, filters) => {
    set(
      produce((state: CatalogQueryState) => {
        const panel = state.panels.find((p) => p.id === panelId)
        if (panel) panel.query.filters = filters
      }),
    )
  },

  setGhosts: (panelId, ghosts) => {
    set(
      produce((state: CatalogQueryState) => {
        if (Object.keys(ghosts).length === 0) delete state.ghosts[panelId]
        else state.ghosts[panelId] = ghosts
      }),
    )
  },

  setShownSearch: (panelId, search) => {
    set(
      produce((state: CatalogQueryState) => {
        state.shownSearch[panelId] = search
      }),
    )
  },

  restore: (snapshot) => set(snapshot),

  resetCatalogQuery: () => set(freshState()),
}))
